import { getEffectiveModelFamily, isDisplacementCategory } from './displacementModelFamily';

type Reading = { cycle?: string; frequency?: number; pressure?: number; displacement?: number };
type TemperatureReading = { condition?: string; temperature?: number; frequency?: number };

// Same key convention the API uses to generate the step-2 fields
// (calibration_data_service.py): point_<value with "." -> "_">_<inc|dec|inc2>,
// and point_0 for the final zero on the decreasing sequence.
const SEQUENCE_SUFFIX = ['inc', 'dec', 'inc2'];

const coordinateText = (value: number) => (Number.isInteger(value) ? `${value}.0` : String(value)).replace('.', '_');

/**
 * Turns a calibration's stored `input_data` back into the flat values the calibration
 * form's dynamic fields use (BOL, AOL, point_*, roomT/roomF, highT/highTempF).
 * The result is a *seed*: the form only applies a key when a field with that key exists.
 */
export function buildDynamicSeed(inputData?: Record<string, any> | null): Record<string, unknown> {
  const seed: Record<string, unknown> = {};
  if (!inputData) return seed;

  if (inputData.BOL != null) seed.BOL = inputData.BOL;

  const readings: Reading[] = Array.isArray(inputData.raw_frequency) ? inputData.raw_frequency : [];
  // The first reading is AOL (see _build_raw_frequency); the rest follow the
  // increasing -> decreasing -> second increasing sequences in order.
  if (readings[0]?.frequency != null) seed.AOL = readings[0].frequency;

  let sequence = -1;
  let previousCycle: string | undefined;
  readings.slice(1).forEach((reading) => {
    if (reading.frequency == null) return;
    if (reading.cycle !== previousCycle) { sequence += 1; previousCycle = reading.cycle; }
    const suffix = SEQUENCE_SUFFIX[sequence];
    const coordinate = reading.pressure ?? reading.displacement;
    if (!suffix || coordinate == null) return;
    const key = suffix === 'dec' && Math.abs(coordinate) < 1e-6 ? 'point_0' : `point_${coordinateText(coordinate)}_${suffix}`;
    seed[key] = reading.frequency;
  });

  const temperatures: TemperatureReading[] = Array.isArray(inputData.temperature_frequency) ? inputData.temperature_frequency : [];
  temperatures.forEach((row) => {
    if (row.temperature == null || row.frequency == null) return;
    if (row.condition === 'room') { seed.roomT = row.temperature; seed.roomF = row.frequency; }
    if (row.condition === 'high') { seed.highT = row.temperature; seed.highTempF = row.frequency; }
  });

  return seed;
}

export type FieldRule = { match: string; value: unknown };

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== '';

/**
 * Rules the runtime form uses to fill its fixed fields, matched against each field's label
 * (case-insensitive regex source). Nothing is stored in the form schema, so a renamed label
 * simply stops matching. Order matters: the most specific rule goes first, and each rule
 * fills at most one field (see `buildPrefill` in formBuilder/prefill.ts).
 */
export function buildFieldRules(
  sensorType: { category?: string; model?: string; measurement_range?: number | null; unit?: string; meta_data?: any } | undefined,
  serialNumber: string | undefined,
  inputData?: Record<string, any> | null,
): FieldRule[] {
  const range = sensorType?.measurement_range != null ? `${sensorType.measurement_range}${sensorType.unit || ''}` : undefined;
  const family = sensorType && isDisplacementCategory(sensorType.category) ? getEffectiveModelFamily(sensorType as any) : undefined;
  const rules: FieldRule[] = [
    { match: String.raw`displacement\s*sensor\s*type|model\s*family`, value: family },
    { match: String.raw`^\s*model\s*$`, value: sensorType?.model },
    { match: String.raw`\brange\b`, value: range },
    { match: String.raw`serial\s*(no\.?|number)`, value: serialNumber },
    { match: 'dwt.*certificate', value: inputData?.dwt_traceable_certificate_no },
    { match: 'equipment.*certificate', value: inputData?.equipment_calibration_certificate_no },
  ];
  return rules.filter((rule) => hasValue(rule.value));
}
